/**
 * ResultScreen.tsx - Results display for the Loss-Gain Framing experiment (Unified Design)
 * Last updated: 2025-08-13 09:56:00
 * Updated by: JulianPolly
 */

import React, { useEffect, useState, useMemo } from "react";
import { Bar, BarChart, Tooltip, XAxis, YAxis, ResponsiveContainer, LabelList, Cell } from "recharts";
import { useSession } from "../../../SessionContext";
import { ScreenConfig } from "../Sequences";
import { LGFramingKey } from "./LGFramingSequence";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, commonStyles, chartColors } from "../../../../components/colors";

// Unified card styles
const cardStyle: React.CSSProperties = {
    ...commonStyles.card,
    margin: 0,
    minWidth: 470,
    maxWidth: 650,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    minHeight: 250
};

const resultMessageStyle: React.CSSProperties = {
    ...commonStyles.card,
    marginTop: 32,
    textAlign: "center",
    fontSize: 17,
    background: colors.backgroundLight,
    maxWidth: 650,
    marginLeft: "auto",
    marginRight: "auto",
};

const chartBlockStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    gap: 24,
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8
};

const chartStyle: React.CSSProperties = {
    minWidth: 180,
    maxWidth: 550,
    flex: 1
};

const gainColor = chartColors.primary[1] || "#238551";
const lossColor = "#D7263D";

// Custom Tooltip with percentages
const CustomTooltip = ({ active, payload, label, total }: any) => {
    if (active && payload && payload.length) {
        const value = payload[0].value;
        const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
        return (
            <div style={{
                background: "#fff",
                border: `1px solid ${colors.silver}`,
                padding: 12,
                borderRadius: 8,
                fontSize: 14,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
            }}>
                <div style={{
                    fontWeight: 600,
                    marginBottom: 6,
                    color: colors.brillantBlue
                }}>
                    {label}
                </div>
                <div style={{ color: payload[0].color || colors.textMedium }}>
                    {value} ({percentage}%)
                </div>
            </div>
        );
    }
    return null;
};

export const ResultScreenComponent = () => {
    const { getGroupData, userData } = useSession();
    const { t, language } = useLanguage();
    const [gainData, setGainData] = useState<any[]>([]);
    const [lossData, setLossData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalGain, setTotalGain] = useState(0);
    const [totalLoss, setTotalLoss] = useState(0);

    // Feste Definition der sliderLabels mit useMemo
    const sliderLabels = useMemo(() => {
        return language === 'en'
            ? [
                "very unlikely",
                "somewhat unlikely",
                "undecided",
                "somewhat likely",
                "very likely"
            ]
            : [
                "sehr unwahrscheinlich",
                "eher unwahrscheinlich",
                "unentschieden",
                "eher wahrscheinlich",
                "sehr wahrscheinlich"
            ];
    }, [language]);

    // Übersetzungen mit Fallbacks
    const gainFrame = t('experiments', 'LGFraming', 'screens', 'screen1', 'gainFrame') ||
        (language === 'en'
            ? "\"If you purchase insurance, your phone is protected against most damages – for example, if you drop it once and the glass breaks. In that case, either the repair will be paid for or you'll quickly receive a replacement device.\""
            : "\"Wenn du eine Versicherung abschließt, ist dein Handy gegen die meisten Schäden abgesichert – zum Beispiel, wenn es dir einmal herunterfällt und das Glas kaputt geht. In dem Fall wird entweder die Reparatur bezahlt oder du erhältst schnell ein Ersatzgerät.\"");

    const lossFrame = t('experiments', 'LGFraming', 'screens', 'screen1', 'lossFrame') ||
        (language === 'en'
            ? "\"If you don't purchase insurance, you could have very bad luck if your phone falls. The repair would then be very costly, and you might even have to buy a new device.\""
            : "\"Wenn du keine Versicherung abschließt, kannst du großes Pech haben, wenn dein Handy herunterfällt. Die Reparatur ist dann sehr kostspielig, und möglicherweise musst du dir sogar ein neues Gerät kaufen.\"");

    const gainTitle = t('experiments', 'LGFraming', 'screens', 'results', 'gainTitle') ||
        (language === 'en' ? "Insurance Assessment (Gain Frame)" : "Versicherungs-Bewertung (Gain-Frame)");

    const lossTitle = t('experiments', 'LGFraming', 'screens', 'results', 'lossTitle') ||
        (language === 'en' ? "Insurance Assessment (Loss Frame)" : "Versicherungs-Bewertung (Loss-Frame)");

    const resultsTitle = t('experiments', 'LGFraming', 'screens', 'results', 'title') ||
        (language === 'en' ? "Results: Framing Influences Decisions" : "Ergebnisse: Framing beeinflusst Entscheidungen");

    const loadingText = t('experiments', 'LGFraming', 'screens', 'results', 'loading') ||
        (language === 'en' ? "Loading results..." : "Lade Ergebnisse…");

    const noGroupText = t('experiments', 'LGFraming', 'screens', 'results', 'userInfo', 'noGroup') ||
        (language === 'en' ? "It could not be determined which group you were in." : "Es konnte nicht ermittelt werden, in welcher Gruppe du warst.");

    // Eigene Bedingung aus userData ermitteln
    let eigeneBedingung: "Bedingung 1" | "Bedingung 2" | undefined = undefined;
    if (userData && userData[LGFramingKey]) {
        const eigeneFrames = Object.values(userData[LGFramingKey]);
        for (const result of eigeneFrames) {
            if (
                result &&
                typeof result === "object" &&
                "selections" in result &&
                Array.isArray((result as any).selections) &&
                (result as any).selections.length > 0
            ) {
                const selection = (result as any).selections[(result as any).selections.length - 1];
                if (
                    selection &&
                    typeof selection.bedingung === "string" &&
                    (selection.bedingung === "Bedingung 1" || selection.bedingung === "Bedingung 2")
                ) {
                    eigeneBedingung = selection.bedingung;
                    break;
                }
            }
        }
    }

    // Variablen für Satz bestimmen
    let gruppenName = "";
    let vorteilNachteil = "";
    let entscheidung = "";

    if (eigeneBedingung === "Bedingung 1") {
        gruppenName = t('experiments', 'LGFraming', 'screens', 'results', 'userInfo', 'group1') ||
            (language === 'en' ? "Group 1" : "Gruppe 1");
        vorteilNachteil = t('experiments', 'LGFraming', 'screens', 'results', 'userInfo', 'advantages') ||
            (language === 'en' ? "advantages" : "Vorteile");
        entscheidung = t('experiments', 'LGFraming', 'screens', 'results', 'userInfo', 'forInsurance') ||
            (language === 'en' ? "for purchasing" : "für den Abschluss");
    } else if (eigeneBedingung === "Bedingung 2") {
        gruppenName = t('experiments', 'LGFraming', 'screens', 'results', 'userInfo', 'group2') ||
            (language === 'en' ? "Group 2" : "Gruppe 2");
        vorteilNachteil = t('experiments', 'LGFraming', 'screens', 'results', 'userInfo', 'disadvantages') ||
            (language === 'en' ? "disadvantages" : "Nachteile");
        entscheidung = t('experiments', 'LGFraming', 'screens', 'results', 'userInfo', 'againstInsurance') ||
            (language === 'en' ? "against purchasing" : "gegen den Abschluss");
    }

    // Dynamischer Satz mit Variablen in bold
    let infoText: React.ReactNode = "";
    if (eigeneBedingung === "Bedingung 1" || eigeneBedingung === "Bedingung 2") {
        const infoTemplate = t('experiments', 'LGFraming', 'screens', 'results', 'userInfo', 'intro') ||
            (language === 'en'
                ? "You were randomly in <b>{group}</b> and were presented with the <b>{framingType}</b> of the decision <b>{direction}</b> insurance."
                : "Du warst zufällig in <b>{group}</b> und dir wurden die <b>{framingType}</b> der Entscheidung <b>{direction}</b> einer Versicherung präsentiert bekommen.");

        const filledText = infoTemplate
            .replace('{group}', gruppenName)
            .replace('{framingType}', vorteilNachteil)
            .replace('{direction}', entscheidung);

        infoText = <span dangerouslySetInnerHTML={{ __html: filledText }} />;
    } else {
        infoText = noGroupText;
    }

    useEffect(() => {
        setLoading(true);
        getGroupData().then(data => {
            const gainCounts = [0, 0, 0, 0, 0];
            const lossCounts = [0, 0, 0, 0, 0];

            data?.forEach((dat: any) => {
                if (dat[LGFramingKey]) {
                    Object.values(dat[LGFramingKey]).forEach((result: any) => {
                        if (result?.selections?.length > 0) {
                            const selection = result.selections[result.selections.length - 1];
                            if (
                                typeof selection.sliderValue === "number" &&
                                selection.sliderValue >= 0 &&
                                selection.sliderValue <= 4
                            ) {
                                if (selection.bedingung === "Bedingung 1") {
                                    gainCounts[selection.sliderValue]++;
                                }
                                if (selection.bedingung === "Bedingung 2") {
                                    lossCounts[selection.sliderValue]++;
                                }
                            }
                        }
                    });
                }
            });

            setGainData(
                sliderLabels.map((label, idx) => ({
                    name: label,
                    Anzahl: gainCounts[idx],
                    color: gainColor
                }))
            );
            setLossData(
                sliderLabels.map((label, idx) => ({
                    name: label,
                    Anzahl: lossCounts[idx],
                    color: lossColor
                }))
            );
            setTotalGain(gainCounts.reduce((a, b) => a + b, 0));
            setTotalLoss(lossCounts.reduce((a, b) => a + b, 0));
            setLoading(false);
        }).catch(error => {
            setLoading(false);
            console.error("Error fetching group data:", error);
        });
    }, [getGroupData, sliderLabels]);

    if (loading) {
        return <div style={{
            textAlign: "center",
            marginTop: 50,
            fontSize: 17,
            color: colors.brillantBlue
        }}>
            {loadingText}
        </div>;
    }

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <h2 style={{
                ...commonStyles.heading,
                margin: "0 0 24px 0",
                fontSize: 28,
                fontWeight: 700
            }}>
                {resultsTitle}
            </h2>
            <div style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 48,
                marginTop: 28
            }}>
                {/* Results Chart - Gain */}
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 12
                    }}>
                        {gainTitle}
                    </h3>
                    <div style={{
                        fontSize: 15,
                        color: colors.textDark,
                        fontStyle: "italic",
                        marginBottom: 16
                    }}>
                        {gainFrame}
                    </div>
                    <div style={chartBlockStyle}>
                        <div style={{ ...chartStyle, margin: "0 auto" }}>
                            <ResponsiveContainer width="100%" height={320}>
                                <BarChart data={gainData}>
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 14, angle: -30, textAnchor: "end" } as any}
                                        interval={0}
                                        height={90}
                                    />
                                    <YAxis allowDecimals={false} domain={[0, 'dataMax + 1']} />
                                    <Tooltip
                                        content={<CustomTooltip total={totalGain} />}
                                        cursor={{ fill: 'rgba(200, 200, 200, 0.2)' }}
                                    />
                                    <Bar dataKey="Anzahl" barSize={50}>
                                        {gainData.map((entry) => (
                                            <Cell key={entry.name} fill={gainColor} />
                                        ))}
                                        <LabelList
                                            dataKey="Anzahl"
                                            position="top"
                                            fontSize={14}
                                            fill={colors.brillantBlue}
                                            fontWeight={600}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
                {/* Results Chart - Loss */}
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 12
                    }}>
                        {lossTitle}
                    </h3>
                    <div style={{
                        fontSize: 15,
                        color: colors.textDark,
                        fontStyle: "italic",
                        marginBottom: 16
                    }}>
                        {lossFrame}
                    </div>
                    <div style={chartBlockStyle}>
                        <div style={{ ...chartStyle, margin: "0 auto" }}>
                            <ResponsiveContainer width="100%" height={320}>
                                <BarChart data={lossData}>
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 14, angle: -30, textAnchor: "end" } as any}
                                        interval={0}
                                        height={90}
                                    />
                                    <YAxis allowDecimals={false} domain={[0, 'dataMax + 1']} />
                                    <Tooltip
                                        content={<CustomTooltip total={totalLoss} />}
                                        cursor={{ fill: 'rgba(200, 200, 200, 0.2)' }}
                                    />
                                    <Bar dataKey="Anzahl" barSize={50}>
                                        {lossData.map((entry) => (
                                            <Cell key={entry.name} fill={lossColor} />
                                        ))}
                                        <LabelList
                                            dataKey="Anzahl"
                                            position="top"
                                            fontSize={14}
                                            fill={colors.brillantBlue}
                                            fontWeight={600}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>
            {/* One sentence for the user under the chart */}
            <div
                style={resultMessageStyle}
            >
                {infoText}
            </div>
        </div>
    );
};

export const ResultScreen: ScreenConfig = {
    component: ResultScreenComponent,
    defaultResult: {
        selections: [],
    },
    canContinueDefault: true,
};